import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { usersService } from '../services/api';
import { 
  User, Mail, Phone, Shield, Calendar, Edit2, Save, X, 
  Lock, Eye, EyeOff, CheckCircle, AlertCircle, Camera,
  Activity, Clock, MapPin, Award, Key
} from 'lucide-react';

const Profile = () => {
  const { user: currentUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const [formData, setFormData] = useState({
    full_name: currentUser?.full_name || '',
    email: currentUser?.email || '',
    phone: currentUser?.phone || '',
  });

  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false
  });

  const [stats, setStats] = useState({
    totalTransactions: 0,
    lastLogin: null,
    accountAge: 0
  });

  // Vérifier si l'utilisateur est administrateur
  const isAdmin = currentUser?.role === 'administrateur';

  useEffect(() => {
    calculateStats();
  }, [currentUser]);

  const calculateStats = () => {
    if (currentUser?.created_at) {
      const createdDate = new Date(currentUser.created_at);
      const now = new Date();
      const diffTime = Math.abs(now - createdDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      setStats({
        totalTransactions: 0,
        lastLogin: new Date(),
        accountAge: diffDays
      });
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleEdit = () => {
    if (!isAdmin) {
      showMessage('error', 'Seul l\'administrateur peut modifier les profils');
      return;
    }
    setIsEditing(true);
  };
  
  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      full_name: currentUser?.full_name || '',
      email: currentUser?.email || '',
      phone: currentUser?.phone || '',
    });
  };

  const handleSave = async () => {
    if (!isAdmin) {
      showMessage('error', 'Seul l\'administrateur peut modifier les profils');
      return;
    }

    setLoading(true);
    try {
      const response = await usersService.updateProfile(currentUser.id, formData);
      
      showMessage('success', 'Profil mis à jour avec succès');
      setIsEditing(false);
      
      // Mettre à jour le localStorage avec les nouvelles données
      const updatedUser = { ...currentUser, ...formData };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      // Recharger la page pour appliquer les changements
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Erreur lors de la mise à jour du profil';
      showMessage('error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

const handleChangePassword = async () => {
  if (!isAdmin) {
    showMessage('error', 'Seul l’administrateur peut changer le mot de passe');
    return;
  }

  // Vérifications habituelles
  if (!passwordData.oldPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
    showMessage('error', 'Veuillez remplir tous les champs');
    return;
  }

  if (passwordData.newPassword !== passwordData.confirmPassword) {
    showMessage('error', 'Les nouveaux mots de passe ne correspondent pas');
    return;
  }

  if (passwordData.newPassword.length < 6) {
    showMessage('error', 'Le mot de passe doit contenir au moins 6 caractères');
    return;
  }

  setLoading(true);
  try {
    await usersService.changePassword({
      oldPassword: passwordData.oldPassword,
      newPassword: passwordData.newPassword
    });
    
    showMessage('success', 'Mot de passe modifié avec succès');
    setIsChangingPassword(false);
    setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
  } catch (error) {
    const errorMessage = error.response?.data?.detail || 'Erreur lors du changement de mot de passe';
    showMessage('error', errorMessage);
  } finally {
    setLoading(false);
  }
};

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const getRoleBadge = (role) => {
    const configs = {
      administrateur: { bg: 'bg-red-500', text: 'Administrateur', icon: Shield },
      gestionnaire: { bg: 'bg-blue-500', text: 'Gestionnaire', icon: Award },
      operateur: { bg: 'bg-green-500', text: 'Opérateur', icon: User },
      lecture: { bg: 'bg-gray-500', text: 'Lecture', icon: Eye }
    };
    return configs[role] || configs.lecture;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Non disponible';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const roleConfig = getRoleBadge(currentUser?.role);
  const RoleIcon = roleConfig.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 transition-colors duration-200">
      <div className="max-w-7xl mx-auto p-6">
        
        {/* Message de feedback */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 animate-in slide-in-from-top ${
            message.type === 'success' 
              ? 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-300 border border-green-200 dark:border-green-800' 
              : 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-300 border border-red-200 dark:border-red-800'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <span className="font-medium">{message.text}</span>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Colonne gauche - Carte principale */}
          <div className="lg:col-span-2 space-y-6">
            {/* Carte Profil */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              {/* Banner gradient */}
              <div className="relative h-32 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600">
                <div className="absolute inset-0 bg-black/10"></div>
              </div>
              
              <div className="px-6 pb-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between -mt-16 mb-6">
                  <div className="flex items-end space-x-4 mb-4 sm:mb-0">
                    {/* Avatar */}
                    <div className="relative">
                      <div className="w-32 h-32 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center border-4 border-white dark:border-gray-800 shadow-2xl">
                        <User className="w-16 h-16 text-white" />
                      </div>
                      {isAdmin && (
                        <button className="absolute bottom-0 right-0 w-10 h-10 bg-white dark:bg-gray-700 rounded-full shadow-lg flex items-center justify-center border-2 border-gray-100 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">
                          <Camera className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                        </button>
                      )}
                    </div>
                    
                    <div className="mb-2">
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {currentUser?.full_name || currentUser?.username}
                      </h2>
                      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full mt-2 ${roleConfig.bg} text-white`}>
                        <RoleIcon className="w-4 h-4" />
                        <span className="text-sm font-medium">{roleConfig.text}</span>
                      </div>
                    </div>
                  </div>

                  {/* Boutons d'action - Uniquement pour les admins */}
                  {isAdmin && (
                    !isEditing ? (
                      <button
                        onClick={handleEdit}
                        className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                      >
                        <Edit2 className="w-4 h-4" />
                        Modifier
                      </button>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          onClick={handleSave}
                          disabled={loading}
                          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 transition-all shadow-lg hover:shadow-xl disabled:opacity-50"
                        >
                          <Save className="w-4 h-4" />
                          {loading ? 'Enregistrement...' : 'Enregistrer'}
                        </button>
                        <button
                          onClick={handleCancel}
                          className="flex items-center gap-2 px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-all"
                        >
                          <X className="w-4 h-4" />
                          Annuler
                        </button>
                      </div>
                    )
                  )}
                </div>

                {/* Informations du profil */}
                <div className="grid md:grid-cols-2 gap-6 mt-6">
                  {/* Nom complet */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">
                      Nom complet
                    </label>
                    {isEditing && isAdmin ? (
                      <input
                        type="text"
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    ) : (
                      <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
                        <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        <span className="text-gray-900 dark:text-white font-medium">{currentUser?.full_name || 'Non renseigné'}</span>
                      </div>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">
                      Email
                    </label>
                    {isEditing && isAdmin ? (
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    ) : (
                      <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
                        <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        <span className="text-gray-900 dark:text-white font-medium">{currentUser?.email}</span>
                      </div>
                    )}
                  </div>

                  {/* Téléphone */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">
                      Téléphone
                    </label>
                    {isEditing && isAdmin ? (
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    ) : (
                      <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
                        <Phone className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        <span className="text-gray-900 dark:text-white font-medium">{currentUser?.phone || 'Non renseigné'}</span>
                      </div>
                    )}
                  </div>

                  {/* Nom d'utilisateur */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">
                      Nom d'utilisateur
                    </label>
                    <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
                      <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      <span className="text-gray-900 dark:text-white font-medium">{currentUser?.username}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Carte Sécurité */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-pink-600 rounded-xl flex items-center justify-center">
                    <Lock className="w-5 h-5 text-white" />
                  </div>
                  Sécurité du compte
                </h3>
                
                {!isChangingPassword && (
                  <button
                    onClick={() => setIsChangingPassword(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl text-sm font-medium"
                  >
                    <Key className="w-4 h-4" />
                    Changer le mot de passe
                  </button>
                )}
              </div>

              {isChangingPassword ? (
                <div className="space-y-4">
                  {/* Ancien mot de passe */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">
                      Ancien mot de passe
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.old ? 'text' : 'password'}
                        name="oldPassword"
                        value={passwordData.oldPassword}
                        onChange={handlePasswordChange}
                        className="w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="Entrez votre ancien mot de passe"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({...showPasswords, old: !showPasswords.old})}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        {showPasswords.old ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {/* Nouveau mot de passe */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">
                      Nouveau mot de passe
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.new ? 'text' : 'password'}
                        name="newPassword"
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        className="w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="Entrez votre nouveau mot de passe"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {/* Confirmer mot de passe */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">
                      Confirmer le mot de passe
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.confirm ? 'text' : 'password'}
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        className="w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="Confirmez votre nouveau mot de passe"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {/* Boutons */}
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={handleChangePassword}
                      disabled={loading}
                      className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 font-medium"
                    >
                      <CheckCircle className="w-5 h-5" />
                      {loading ? 'Modification...' : 'Confirmer'}
                    </button>
                    <button
                      onClick={() => {
                        setIsChangingPassword(false);
                        setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
                      }}
                      className="flex-1 px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-all font-medium"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-gray-600 dark:text-gray-400">
                  Dernière modification : {formatDate(currentUser?.updated_at)}
                </p>
              )}
            </div>
          </div>

          {/* Colonne droite - Stats et infos */}
          <div className="space-y-6">
            {/* Statut du compte */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">
                Statut du compte
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                  <span className="text-gray-700 dark:text-gray-300 font-medium">État</span>
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${currentUser?.is_active ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
                    <span className={`font-semibold ${currentUser?.is_active ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {currentUser?.is_active ? 'Actif' : 'Inactif'}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                  <span className="text-gray-700 dark:text-gray-300 font-medium">Rôle</span>
                  <span className="text-gray-900 dark:text-white font-semibold capitalize">
                    {currentUser?.role}
                  </span>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                  <span className="text-gray-700 dark:text-gray-300 font-medium">Date de création</span>
                  <span className="text-gray-900 dark:text-white font-semibold text-sm">
                    {new Date(currentUser?.created_at).toLocaleDateString('fr-FR')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;